import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "../supabase/supabase-client";
import { FinanceCategory, FinanceTransaction } from "../types/entities";
import { TablesInsert, TablesUpdate } from "../types/supabase";

export const FINANCE_QUERY_KEY = ["finance_transactions"];

/**
 * The whole ledger, oldest first.
 *
 * Deliberately unpaginated: the balance is a running total, so any view of it
 * needs every row that came before anyway, and the loge books on the order of
 * twenty movements a year. Ordered ascending because that is the order the
 * running total is computed in — the list screen reverses it for display.
 */
export const useFinanceTransactions = () => {
  return useQuery({
    queryKey: FINANCE_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_transactions")
        .select("*")
        .eq("deleted", false)
        .order("occurred_on", { ascending: true })
        .order("id", { ascending: true });
      if (error) throw error;
      return data as FinanceTransaction[];
    },
  });
};

export const useGetFinanceTransaction = (transactionId?: string) => {
  return useQuery({
    queryKey: ["finance_transaction", transactionId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_transactions")
        .select("*")
        .eq("id", Number(transactionId))
        .maybeSingle();
      if (error) throw error;
      return data as FinanceTransaction | null;
    },
    enabled: !!transactionId,
  });
};

export interface FinanceTransactionPayload {
  /** `YYYY-MM-DD`. */
  occurredOn: string;
  /** Signed, in øre — negative is money out. See the table's column comment. */
  amountOre: number;
  category: FinanceCategory;
  note?: string | null;
  logeEventId?: number | null;
  fineId?: number | null;
  logeMemberId?: number | null;
  createdBy?: number | null;
}

const toRow = (p: FinanceTransactionPayload): TablesInsert<"finance_transactions"> => ({
  occurred_on: p.occurredOn,
  amount_ore: p.amountOre,
  category: p.category,
  note: p.note?.trim() || null,
  loge_event_id: p.logeEventId ?? null,
  fine_id: p.fineId ?? null,
  loge_member_id: p.logeMemberId ?? null,
  created_by: p.createdBy ?? null,
});

export const useAddFinanceTransaction = () => {
  return useMutation(async (payload: FinanceTransactionPayload) => {
    const { data, error } = await supabase
      .from("finance_transactions")
      .insert(toRow(payload))
      .select()
      .single();
    if (error) throw error;
    return data as FinanceTransaction;
  });
};

export const useUpdateFinanceTransaction = () => {
  return useMutation(
    async ({ id, payload }: { id: number; payload: FinanceTransactionPayload }) => {
      // created_by records who first booked it, so an edit leaves it alone.
      const { created_by, ...updates } = toRow(payload);
      const { error } = await supabase
        .from("finance_transactions")
        .update(updates as TablesUpdate<"finance_transactions">)
        .eq("id", id);
      if (error) throw error;
    }
  );
};

/** Soft delete, like fines: a ledger that forgets rows can't explain a balance. */
export const useDeleteFinanceTransaction = () => {
  return useMutation(async (id: number) => {
    const { error } = await supabase
      .from("finance_transactions")
      .update({ deleted: true })
      .eq("id", id);
    if (error) throw error;
  });
};
